import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import globalUserProfile from '../../../../stores/global-user-profile';
import { StudentProfile } from '../../../../api/types/settings/settings-all';
import { CourseWithAbstractDetails } from '../../../../api/types/students/courses/student-courses';
import { CourseStatus } from '../../../../api/types/programs/course/course-status';
import { CourseNotesDialog } from './course-notes-dialog';

export const MyCoursesStudent = observer(() => {
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [selectedCourseNotes, setSelectedCourseNotes] = useState('');
  const [selectedCourseName, setSelectedCourseName] = useState('');
  // Check if user profile exists and is a student
  if (!globalUserProfile.userProfile?.profile) {
    return <div>Loading user profile...</div>;
  }

  const studentProfile = globalUserProfile.userProfile
    .profile as StudentProfile;
  const courses: CourseWithAbstractDetails[] = studentProfile.courses;

  if (!courses || courses.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>הקורסים שלי</h2>
        <p>לא נמצאו קורסים רשומים</p>
      </div>
    );
  }

  // Template functions for DataTable columns
  const statusBodyTemplate = (course: CourseWithAbstractDetails) => {
    return (
      <Tag
        value={CourseStatus.getDisplayText(course.status)}
        severity={getStatusSeverity(course.status)}
        style={{ fontSize: '12px' }}
      />
    );
  };

  const attendanceBodyTemplate = (course: CourseWithAbstractDetails) => {
    return `${course.attendancePercentage}%`;
  };

  /*const programYearBodyTemplate = (course: CourseWithAbstractDetails) => {
    return course.programYear || '-';
  };*/

  const startDateBodyTemplate = (course: CourseWithAbstractDetails) => {
    return course.courseStartDate
      ? new Date(course.courseStartDate).toLocaleDateString('he-IL')
      : '-';
  };

  const notesBodyTemplate = (course: CourseWithAbstractDetails) => {
    return (
      <div style={{ textAlign: 'center' }}>
        <i
          className="pi pi-info-circle course-notes-info-icon"
          style={{
            fontSize: '16px',
            color: '#2196F3',
            cursor: 'pointer',
          }}
          onClick={() => {
            setSelectedCourseName(course.courseName);
            setSelectedCourseNotes(course.notes || '');
            setShowNotesDialog(true);
          }}
        />
        <Tooltip
          target=".course-notes-info-icon"
          content="למידע נוסף אודות הקורס"
        />
      </div>
    );
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>הקורסים שלי</h2>
      <DataTable
        value={courses}
        stripedRows
        showGridlines
        tableStyle={{ minWidth: '50rem' }}
        rows={10}
        style={{ marginTop: '16px' }}
      >
        <Column
          field="courseName"
          header="שם הקורס"
          style={{ textAlign: 'right', fontWeight: 'bold' }}
        />
        <Column
          header="סטטוס קורס"
          body={statusBodyTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          field="courseHours"
          header="שעות קורס"
          style={{ textAlign: 'right' }}
        />
        <Column
          header="אחוז נוכחות"
          body={attendanceBodyTemplate}
          style={{ textAlign: 'right' }}
        />
        {/*<Column
          header="שנת תוכנית"
          body={programYearBodyTemplate}
          style={{ textAlign: 'right' }}
        />*/}
        <Column
          header="תאריך התחלה"
          body={startDateBodyTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="מידע"
          body={notesBodyTemplate}
          style={{ textAlign: 'center', width: '80px' }}
        />
      </DataTable>

      <CourseNotesDialog
        visible={showNotesDialog}
        onHide={() => setShowNotesDialog(false)}
        courseName={selectedCourseName}
        notes={selectedCourseNotes}
      />
    </div>
  );
});

// Helper function to get PrimeReact Tag severity for course status
const getStatusSeverity = (
  status: CourseStatus,
): 'success' | 'info' | 'warning' | 'danger' | null => {
  switch (status) {
    case CourseStatus.ACTIVE:
      return 'success'; // Green
    case CourseStatus.INIT:
      return 'info'; // Blue
    case CourseStatus.FINISHED:
      return 'warning'; // Orange
    case CourseStatus.CLOSED:
      return 'danger'; // Red
    default:
      return null; // Default
  }
};
